import { Controller } from "@hotwired/stimulus";

// Snapshots and restores scroll position for an internal scroll surface.
//
// SidebarLayoutComponent is viewport-locked (h-svh overflow-hidden) so
// flush surfaces (chat, editor) can size their internal panes against a
// fixed parent. Padded pages then scroll inside App::PageComponent
// (overflow-auto) instead of the document, which means Turbo's default
// window.scrollTo restoration doesn't bring the user back to where they
// left off on a back navigation. This controller fills that gap.
//
// Keyed by path+search so two routes with different query strings
// snapshot independently. Module-scoped Map survives controller
// disconnects (Turbo Drive replaces the element on every nav). Capped
// FIFO so long sessions don't accumulate state without bound.
const MAX_ENTRIES = 50;
const positions = new Map();

function remember(key, value) {
  if (positions.has(key)) {
    positions.delete(key);
  } else if (positions.size >= MAX_ENTRIES) {
    positions.delete(positions.keys().next().value);
  }
  positions.set(key, value);
}

export default class extends Controller {
  initialize() {
    this.onScroll = this.onScroll.bind(this);
    this.onBeforeCache = this.onBeforeCache.bind(this);
  }

  connect() {
    const saved = positions.get(this.key);
    if (saved !== undefined) {
      this.element.scrollTop = saved;
    }

    document.addEventListener("turbo:before-cache", this.onBeforeCache);
    this.element.addEventListener("scroll", this.onScroll, { passive: true });
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.onBeforeCache);
    this.element.removeEventListener("scroll", this.onScroll);
  }

  onScroll() {
    remember(this.key, this.element.scrollTop);
  }

  onBeforeCache() {
    remember(this.key, this.element.scrollTop);
  }

  get key() {
    return window.location.pathname + window.location.search;
  }
}
